/**
 * 航段费用「批量删除」弹窗 — 全部删除 / 选费项删除 双分支
 *
 * 承接:主表勾选航段行后点「批量删除」,弹本窗二选一:
 *   - 全部删除:去掉所选航段上的直接费项，以及公共分摊摊到这些航段上的份额（其它飞机仍留在原单上）
 *   - 选费项删除:按「费用类型」归并列出(一类一行,含条数与合计),勾选类型即删这些航段下
 *     该类型的全部费项。跨航段合并:同一类型分布在多个所选航段时勾一次全删;
 *     分摊费项按「本航段份额」删除，不会把其它飞机从这张单上抹掉。
 *
 * 删除口径:直接费用走软删除(隐藏、可追溯);公共费用只拿掉摊到本段的份额,
 *   另一架飞机仍留在原单上,份额拿掉后不能按「隐藏」的方式找回。
 *   若这是这张单的最后一段,整张分摊单会作废。必填删除原因(≥5 字);
 *   受影响的已完成批次会被标记为需重算。
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Modal, Radio, Table, Input, Alert, Typography, Tag, Space, message, Spin, Empty } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { settlementService } from "../../../../api/enhancedApi";

const { Text } = Typography;
const { TextArea } = Input;

const MIN_REASON_LENGTH = 5;

const formatMoney = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "0.00";
    return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const BatchDeleteModal = ({ visible, flightOperationIds = [], onClose, onSuccess }) => {
    const [mode, setMode] = useState("all"); // all | select
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedCostTypes, setSelectedCostTypes] = useState([]);
    const [deleteReason, setDeleteReason] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const loadItems = useCallback(async () => {
        if (!flightOperationIds || flightOperationIds.length === 0) return;
        setLoading(true);
        try {
            const res = await settlementService.legCost.getLegCostItemsByFlights(flightOperationIds);
            setItems(res?.data?.items || []);
        } catch (err) {
            message.error("加载费项清单失败: " + (err.message || "未知错误"));
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [flightOperationIds]);

    // 打开时重置状态并加载费项清单;关闭时清空,避免下次闪旧数据
    useEffect(() => {
        if (visible) {
            setMode("all");
            setSelectedCostTypes([]);
            setDeleteReason("");
            loadItems();
        } else {
            setItems([]);
        }
    }, [visible, loadItems]);

    // 按费用类型归并所选航段的费项:一类一行(含条数/合计/底层费项 id),
    // 勾选类型即删该类型下全部费项(跨所选航段合并)
    const costTypeGroups = useMemo(() => {
        const map = new Map();
        for (const it of items) {
            const key = it.cost_type || "UNKNOWN";
            if (!map.has(key)) {
                map.set(key, { cost_type: key, count: 0, amount: 0, ids: [] });
            }
            const g = map.get(key);
            g.count += 1;
            g.amount += Number(it.base_total_amount || 0);
            g.ids.push(it.id);
        }
        return Array.from(map.values());
    }, [items]);

    const columns = [
        {
            title: "费用类型",
            dataIndex: "cost_type",
            render: (v) => (v ? <Tag>{v}</Tag> : "—"),
        },
        {
            title: "条数",
            dataIndex: "count",
            width: 90,
            align: "right",
            render: (v) => `${v} 条`,
        },
        {
            title: "合计金额",
            dataIndex: "amount",
            width: 160,
            align: "right",
            render: (v) => (
                <Text strong style={{ color: "#1677ff" }}>
                    ${formatMoney(v)}
                </Text>
            ),
        },
    ];

    // 勾中的费用类型对应的费项总条数(给按钮文案 + 提交范围提示用)
    const selectedCount = useMemo(
        () =>
            costTypeGroups
                .filter((g) => selectedCostTypes.includes(g.cost_type))
                .reduce((sum, g) => sum + g.count, 0),
        [costTypeGroups, selectedCostTypes],
    );

    const reasonValid = deleteReason.trim().length >= MIN_REASON_LENGTH;
    const selectEmpty = mode === "select" && selectedCostTypes.length === 0;
    // 「全部删除」下这些航段已无 ACTIVE 费项时也没有可删目标 —— 提前禁用，避免点了才被后端 404 挡回
    const allEmpty = mode === "all" && !loading && items.length === 0;
    const nothingToDelete = selectEmpty || allEmpty;

    const handleConfirm = async () => {
        if (!reasonValid) {
            message.warning(`删除原因至少 ${MIN_REASON_LENGTH} 个字符(审计追溯用)`);
            return;
        }
        if (selectEmpty) {
            message.warning("请至少勾选一类要删除的费用");
            return;
        }
        if (allEmpty) {
            message.warning("这些航段下没有可删除的费用");
            return;
        }
        // 全部删除传航段 id(后端展开为该航段全部 ACTIVE 费项);
        // 选费项删除:把勾中的费用类型映射回底层费项 UUID 提交
        const ids =
            mode === "all"
                ? flightOperationIds
                : costTypeGroups
                      .filter((g) => selectedCostTypes.includes(g.cost_type))
                      .flatMap((g) => g.ids);
        setSubmitting(true);
        try {
            const res = await settlementService.legCost.batchDeleteLegCosts(ids, deleteReason.trim());
            if (onSuccess) onSuccess(res?.data || {});
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : detail?.message || err.message;
            message.error("批量删除失败: " + (msg || "未知错误"));
        } finally {
            setSubmitting(false);
        }
    };

    const okText =
        mode === "all"
            ? `全部删除 (${items.length} 条)`
            : `删除选中 (${selectedCostTypes.length} 类 / ${selectedCount} 条)`;

    return (
        <Modal
            title={
                <Space>
                    <ExclamationCircleOutlined style={{ color: "#faad14" }} />
                    批量删除费用
                </Space>
            }
            open={visible}
            onCancel={onClose}
            onOk={handleConfirm}
            okText={okText}
            okType="danger"
            okButtonProps={{ loading: submitting, disabled: loading || !reasonValid || nothingToDelete }}
            cancelText="取消"
            cancelButtonProps={{ disabled: submitting }}
            closable={!submitting}
            maskClosable={!submitting}
            width={mode === "select" ? 620 : 560}
            destroyOnClose
        >
            <Spin spinning={loading} tip="加载费项清单...">
                <Space direction="vertical" style={{ width: "100%" }} size="middle">
                    <Alert
                        type="warning"
                        showIcon
                        message={`已选中 ${flightOperationIds.length} 个航段，共 ${items.length} 条费用明细`}
                        description="直接费用会隐藏、不再计入合计，可追溯。摊到本段的公共费用只从这张分摊单上拿掉，另一架飞机不受影响；拿掉后不能按同样方式恢复。若这是这张单的最后一段，整张分摊单会作废。受影响的已完成批次会被标记为需重算。"
                    />

                    <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)} disabled={submitting}>
                        <Space direction="vertical">
                            <Radio value="all">全部删除（本航段直接费用 + 摊到本段的公共费用份额）</Radio>
                            <Radio value="select">选费项删除（勾选要删的费用类型）</Radio>
                        </Space>
                    </Radio.Group>

                    {mode === "select" &&
                        (costTypeGroups.length > 0 ? (
                            <Table
                                rowKey="cost_type"
                                size="small"
                                columns={columns}
                                dataSource={costTypeGroups}
                                pagination={false}
                                scroll={{ y: 340 }}
                                rowSelection={{
                                    selectedRowKeys: selectedCostTypes,
                                    onChange: setSelectedCostTypes,
                                }}
                            />
                        ) : (
                            <Empty description="这些航段下暂无可删除的费项" />
                        ))}

                    <div>
                        <Text strong style={{ display: "block", marginBottom: 4 }}>
                            删除原因（必填，≥{MIN_REASON_LENGTH} 字）
                        </Text>
                        <TextArea
                            value={deleteReason}
                            onChange={(e) => setDeleteReason(e.target.value)}
                            rows={2}
                            maxLength={500}
                            showCount
                            disabled={submitting}
                            placeholder="例如：录入重复，与 INV-001 重复；或航段作废需清理费用"
                        />
                    </div>
                </Space>
            </Spin>
        </Modal>
    );
};

export default BatchDeleteModal;
